import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Quote presentation only. Delivery stays with the merchant's existing bulk email. */
class AtelierEnquiry extends Component {
  #root; #dialog; #listeners; #opener; #frame; #closeTimer; #closing = false;
  #lastKind = 'sample'; #colour = ''; #touchY = null; #touchDistance = 0;
  #field = name => this.querySelector(`[data-enquiry-${name}]`);
  #reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#root = this.closest('fivepoint-pdp');
    this.#dialog = this.querySelector('dialog');
    if (!this.#root || !this.#dialog) return;
    this.#root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-atelier-enquiry-open]');
      if (trigger) this.open(trigger);
    }, options);
    this.#root.addEventListener('atelier:selection', this.#selection, options);
    this.#dialog.querySelector('[data-atelier-enquiry-close]').addEventListener('click', () => this.close(), options);
    this.#dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
    this.#dialog.addEventListener('close', this.#afterClose, options);
    this.#dialog.addEventListener('click', event => {
      if (event.target !== this.#dialog) return;
      const box = this.#dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
    }, options);
    this.#dialog.addEventListener('transitionend', event => {
      if (this.#closing && event.target === this.#dialog && event.propertyName === 'transform') this.#finishClose();
    }, options);
    this.#field('form').addEventListener('click', this.#formClick, options);
    this.#field('form').addEventListener('submit', this.#submit, options);
    this.#field('form').addEventListener('input', event => {
      if (event.target === this.#field('email')) this.#error('email', '', []);
      if (event.target.type === 'number') this.#error('quantity', '', []);
    }, options);
    this.#dialog.addEventListener('touchstart', this.#touchStart, { ...options, passive: true });
    this.#dialog.addEventListener('touchmove', this.#touchMove, { ...options, passive: false });
    this.#dialog.addEventListener('touchend', this.#touchEnd, options);
    this.#dialog.addEventListener('touchcancel', () => this.#resetTouch(), options);
    const validRecipient = /^[^\s@<>]+@[^\s@<>.]+(?:\.[^\s@<>.]+)+$/.test(this.dataset.email);
    this.#field('send').disabled = !validRecipient;
    if (!validRecipient) this.#status(this.dataset.recipientError);
    this.#selection();
  }
  #selection = event => {
    const kind = event?.detail?.kind || this.#root.querySelector('[data-fp-foot]')?.dataset.tier;
    if (kind === 'bulk') this.open();
    else if (kind) this.#lastKind = kind;
  };
  open(trigger = document.activeElement) {
    if (this.#dialog.open && !this.#closing) return;
    clearTimeout(this.#closeTimer); cancelAnimationFrame(this.#frame);
    this.#closing = false; this.#opener = trigger;
    const selected = this.#root.querySelector('.fp-chips [aria-checked="true"]');
    const other = selected?.classList.contains('fp-chip--other') || selected?.dataset.fpChip === 'OTHER';
    const label = selected?.querySelector('u')?.textContent.trim() || '';
    const code = selected?.dataset.fpChip || '';
    this.#colour = other ? this.#root.querySelector('[data-fp-other-input]')?.value.trim() || '' : code && label && code !== label ? `${code} · ${label}` : label || code;
    this.#field('colour').textContent = this.#colour ? `${this.dataset.colourLabel} ${this.#colour}` : this.dataset.anyColour;
    const image = selected?.querySelector('img') || this.#root.querySelector('.fp-rail img');
    const imageHost = this.#field('image'); imageHost.replaceChildren(); imageHost.hidden = !image;
    if (image) {
      const copy = image.cloneNode(); copy.removeAttribute('id'); copy.alt = ''; copy.width = 72; copy.height = 90; copy.sizes = '72px';
      imageHost.append(copy);
    }
    this.#root.querySelector('[data-atelier-enquiry-open]')?.setAttribute('aria-expanded', 'true');
    lockScroll(this);
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#field('body').scrollTop = 0;
    this.#dialog.focus({ preventScroll: true });
    // Commit the off-screen modal before starting the prototype's 320 ms transition.
    this.#dialog.getBoundingClientRect();
    this.#frame = requestAnimationFrame(() => this.#dialog.classList.add('is-open'));
  }
  close() {
    if (!this.#dialog.open || this.#closing) return;
    cancelAnimationFrame(this.#frame); this.#resetTouch();
    this.#closing = true; this.#dialog.classList.remove('is-open');
    if (this.#reduced()) this.#finishClose();
    else this.#closeTimer = setTimeout(() => this.#finishClose(), 320);
  }
  #finishClose() {
    clearTimeout(this.#closeTimer);
    if (this.#dialog.open) this.#dialog.close();
  }
  #afterClose = () => {
    clearTimeout(this.#closeTimer); cancelAnimationFrame(this.#frame);
    this.#closing = false; this.#dialog.classList.remove('is-open');
    unlockScroll(this);
    this.#root.querySelector('[data-atelier-enquiry-open]')?.setAttribute('aria-expanded', 'false');
    // Native legacy bulk selections still return to their previous purchase kind.
    if (this.#root.querySelector('[data-fp-foot]')?.dataset.tier === 'bulk') this.#root.pick(this.#lastKind);
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
  };
  #formClick = event => {
    const step = event.target.closest('[data-enquiry-step]');
    if (step) {
      const input = this.#field('metres');
      input.value = String(Math.min(99999, Math.max(10, (input.valueAsNumber || 0) + Number(step.dataset.enquiryStep))));
      this.#error('quantity', '', []); return;
    }
    const chip = event.target.closest('[data-enquiry-choice]');
    if (chip) {
      const selected = chip.getAttribute('aria-pressed') === 'true';
      for (const sibling of chip.parentElement.querySelectorAll('[data-enquiry-choice]')) sibling.setAttribute('aria-pressed', 'false');
      chip.setAttribute('aria-pressed', String(!selected)); return;
    }
    if (!event.target.closest('[data-enquiry-range]')) return;
    const range = this.#field('range'), ranged = range.getAttribute('aria-expanded') !== 'true';
    const one = this.#field('metres'), low = this.#field('from'), high = this.#field('to');
    if (ranged && one.valueAsNumber > 0) {
      low.value = one.value;
      if (high.valueAsNumber <= low.valueAsNumber) high.value = String(Math.min(99999, low.valueAsNumber * 2));
    } else if (!ranged) one.value = low.value;
    this.#field('one').hidden = ranged; this.#field('two').hidden = !ranged;
    this.#field('unsure').hidden = ranged; range.setAttribute('aria-expanded', String(ranged));
    range.textContent = ranged ? this.dataset.oneFigure : this.dataset.giveRange;
    this.#error('quantity', '', []); (ranged ? low : one).focus({ preventScroll: true });
  };
  #error(kind, text, inputs) {
    const error = this.#field(`${kind}-error`); error.textContent = text; error.hidden = !text;
    const all = kind === 'email' ? [this.#field('email')] : ['metres', 'from', 'to'].map(this.#field);
    for (const input of all) input.setAttribute('aria-invalid', String(!!text && inputs.includes(input)));
  }
  #status(text) { const status = this.#field('status'); status.textContent = text; status.hidden = !text; }
  #submit = event => {
    event.preventDefault();
    if (this.#field('send').disabled) return;
    const ranged = !this.#field('two').hidden;
    const inputs = (ranged ? ['from', 'to'] : ['metres']).map(this.#field);
    const invalid = inputs.filter(input => input.validity.badInput || (input.value && (!Number.isInteger(input.valueAsNumber) || !input.checkValidity())));
    if (ranged && inputs[0].value && inputs[1].value && inputs[0].valueAsNumber > inputs[1].valueAsNumber) invalid.push(...inputs);
    if (invalid.length) { this.#error('quantity', this.dataset.quantityError, invalid); invalid[0].focus(); return; }
    this.#error('quantity', '', []);
    const email = this.#field('email'); email.value = email.value.trim();
    if (!email.checkValidity() || !/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(email.value)) {
      this.#error('email', this.dataset.emailError, [email]); email.focus(); return;
    }
    this.#error('email', '', []);
    const values = inputs.map(input => input.value).filter(Boolean);
    const metres = values.length ? `${values.join('–')} m` : this.dataset.discuss;
    const choice = key => this.querySelector(`[data-enquiry-choice="${key}"][aria-pressed="true"]`)?.textContent.trim() || '';
    const lines = [window.FivePointI18n.t("Fabric: {{p0}}", [this.dataset.product])];
    if (this.dataset.article) lines.push(window.FivePointI18n.t("Article: {{p0}}", [this.dataset.article]));
    lines.push(window.FivePointI18n.t("Product: {{p0}}", [this.dataset.productUrl]), window.FivePointI18n.t("Colour: {{p0}}", [this.#colour || this.dataset.anyColour]), window.FivePointI18n.t("Metres: {{p0}}", [metres]));
    if (choice('use')) lines.push(window.FivePointI18n.t("For: {{p0}}", [choice('use')]));
    if (choice('when')) lines.push(window.FivePointI18n.t("When: {{p0}}", [choice('when')]));
    lines.push(window.FivePointI18n.t("Reply email: {{p0}}", [email.value]));
    if (this.#field('company').value.trim()) lines.push(window.FivePointI18n.t("Company or name: {{p0}}", [this.#field('company').value.trim()]));
    if (this.#field('notes').value.trim()) lines.push(window.FivePointI18n.t("Notes: {{p0}}", [this.#field('notes').value.trim()]));
    const link = this.#field('mail');
    link.href = `mailto:${this.dataset.email}?subject=${encodeURIComponent(window.FivePointI18n.t("Quote enquiry: {{p0}}", [this.dataset.product]))}&body=${encodeURIComponent(lines.join('\n'))}`;
    link.hidden = false; link.click(); this.#status(this.dataset.emailReady);
  };
  #touchStart = event => {
    if (!matchMedia('(max-width: 767px)').matches || event.touches.length !== 1 || this.#closing) return;
    if (event.target.closest('input,textarea,button,a') || (this.#field('body').contains(event.target) && this.#field('body').scrollTop > 0)) return;
    this.#touchY = event.touches[0].clientY; this.#touchDistance = 0;
  };
  #touchMove = event => {
    if (this.#touchY === null) return;
    this.#touchDistance = Math.max(0, event.touches[0].clientY - this.#touchY);
    if (!this.#touchDistance) return;
    event.preventDefault(); this.#dialog.style.transition = 'none';
    this.#dialog.style.transform = `translateY(${this.#touchDistance}px)`;
  };
  #resetTouch() { this.#touchY = null; this.#touchDistance = 0; this.#dialog.style.transition = ''; this.#dialog.style.transform = ''; }
  #touchEnd = () => { const dismiss = this.#touchDistance > 80; this.#resetTouch(); if (dismiss) this.close(); };
  disconnectedCallback() {
    super.disconnectedCallback(); this.#listeners?.abort();
    clearTimeout(this.#closeTimer); cancelAnimationFrame(this.#frame); unlockScroll(this);
    if (this.#dialog?.open) this.#dialog.close();
  }
}
if (!customElements.get('fivepoint-atelier-enquiry')) customElements.define('fivepoint-atelier-enquiry', AtelierEnquiry);
